import dayjs from 'dayjs';

export const TIME_HH_MM_FORMAT = 'HH:mm';

/**
 * Parse a time value (either an 'HH:mm' string or a Dayjs) into a Dayjs object.
 * Returns `undefined` when the input is falsy or invalid.
 */
export function parseTimeStringToDayjs(val: unknown): dayjs.Dayjs | undefined {
  if (!val && val !== '') return undefined;
  if (dayjs.isDayjs(val)) return val;
  if (typeof val === 'string' && val.trim() !== '') {
    const d = dayjs(val, TIME_HH_MM_FORMAT);
    return d.isValid() ? d : undefined;
  }
  return undefined;
}

/** @deprecated use dateHelpers or dayJs helper */
export const getDateFromISO = (isoDateString: string) => {
  return new Date(isoDateString).toLocaleDateString('en', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

type weekdayType = 'long' | 'short';
/** @deprecated use dateHelpers or dayJs helper */
/**
 * Generates an array of localized weekday names for a specific locale and format.
 * @param {string} [locale='en-GB'] - The locale to use for formatting. Defaults to 'en-GB'.
 * @param {weekdayType} [weekday='long'] - The format of the weekday name. Can be 'long' or 'short'. Defaults to 'long'.
 * @returns {string[]} An array of localized weekday names based on the specified format and locale.
 * @see {@link https://stackoverflow.com/a/66627442} Original implementation on Stack Overflow.
 */
export const generateDaysForLocale = (
  locale = 'en-GB',
  weekday: weekdayType = 'long'
): string[] => {
  const { format } = new Intl.DateTimeFormat(locale, { weekday });
  return [...Array(7).keys()].map((day) =>
    format(new Date(Date.UTC(2021, 5, day)))
  );
};

/** @deprecated use dateHelpers or dayJs helper */
/**
 * Converts an ISO date string to a full time format "HH:MM:ss" if possible, or formats the time
 * from the ISO date string using the system's default time format.
 * @param {string} isoDateString - The ISO date string to convert or format.
 * @returns {string} The time string in "HH:MM:ss" format or the system's default time format.
 */
export const getFullTimeFromISOString = (isoDateString: string): string => {
  if (isFullTimeFormat(isoDateString)) {
    return isoDateString;
  }

  const date = new Date(isoDateString);

  if (isNaN(date.getTime())) {
    return isoDateString;
  }

  return new Intl.DateTimeFormat('default', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
    .format(date)
    .toString();
};

/**
 * Checks if the given value is a string in the full time format "HH:MM:ss".
 * @param {string} value - The value to be checked.
 * @returns {boolean} True if the value is a valid full time format, false otherwise.
 */
export const isFullTimeFormat = (value: string): boolean => {
  if (typeof value !== 'string') return false;

  const timeParts = value.split(':');
  if (timeParts.length !== 3) {
    return false; // Incorrect number of time components
  }

  const [HH, MM, ss] = timeParts.map(Number); // Convert parts to numbers

  if (isNaN(HH) || isNaN(MM) || isNaN(ss)) {
    return false; // Invalid numeric components
  }

  if (HH < 0 || HH > 24 || MM < 0 || MM > 59 || ss < 0 || ss > 59) {
    return false; // Out-of-range components
  }

  return true;
};
